import React from "react";
import {
  TransitionHistory,
  CustomText,
  FallBack,
  CustomTextInput,
  PrimaryCta,
  CustomButton,
  FallBackBtn,
  Image,
  Sidebar,
  CustomTextArea,
  ModalComponent,
  FreeTrailPopup,
} from "../../components";
import SideMenu from "../sideMenu";
import Header from "../header";
import { useState, useEffect, useRef } from "react";
import Core from "../../common/clientSdk";
import Modal from "react-modal";
import { Loader } from "../loader";
import Icon from "../../components/core_component/icon";
import Utils from "../../common/utils";
import CustomTable from "../../components/customTable";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CreateCollectionModal from "../../screens/collection/createCollection";
import { useDispatch, useSelector } from "react-redux";
import CollectionCampaignInfluencerTable from "../../modal/collectionCampaignInfluencer";
import Toggle from "react-toggle";
import Tooltip from "@mui/material/Tooltip";
import PriceToCollectionInfluencer from "../../modal/priceUpdateModal";
import WebStorage from "../../common/web_storage";
import * as WebStorageKey from "../../common/web_storage_keys";
import {
  applyBatchSyncJobsToRows,
  countExistingInfluencerRowsForAddLimit,
  getBatchJobDisplayKind,
  getPlatformIsUpdatedNumberFromObject,
  isBatchJobComplete,
  isBatchSyncFinished,
  makePendingInfluencerId,
  mergeQueuedInfluencerHandles,
  normIdentifier,
  sortInfluencerRowsForSyncDisplay,
} from "../../common/addInfluencerBatchFlow";

const PROGRESS_FLAG_FIELDS = [
  "name",
  "profile_pic",
  "followers",
  "content_count",
  "engagement_rate",
];

function getProgressStatus(statusValue) {
  if (typeof statusValue === "string") return statusValue;
  if (statusValue && typeof statusValue === "object") {
    if (typeof statusValue.status === "string") return statusValue.status;
    const hasAllFlags = PROGRESS_FLAG_FIELDS.every((field) => statusValue[field]);
    return hasAllFlags ? "COMPLETED" : "IN_PROGRESS";
  }
  return undefined;
}

/** Pick platform row for the campaign (object or array). */
function getPlatformDataForCampaign(rawPlatforms, platformId) {
  if (rawPlatforms == null) return {};
  const list = Array.isArray(rawPlatforms) ? rawPlatforms : [rawPlatforms];
  if (platformId) {
    const found = list.find((p) => p && p.platform_id === platformId);
    if (found) return found;
  }
  return list[0] || {};
}

function getPlatformIsUpdatedValue(rawPlatforms, platformId) {
  const platformData = getPlatformDataForCampaign(rawPlatforms, platformId);
  return getPlatformIsUpdatedNumberFromObject(platformData);
}

function readPendingBatchSyncFromStorage(collectionId) {
  if (!collectionId) return null;
  try {
    const raw = WebStorage.getDataFromWebStorage(
      WebStorageKey.ADD_INFLUENCER_BATCH_SYNC_KEY(collectionId),
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.batch_id !== "string" || !parsed.batch_id.trim()) {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

function writePendingBatchSyncToStorage(collectionId, batchId) {
  if (!collectionId || !batchId) return;
  WebStorage.saveDataInWebStorage(
    WebStorageKey.ADD_INFLUENCER_BATCH_SYNC_KEY(collectionId),
    JSON.stringify({ batch_id: String(batchId) }),
  );
}

function clearPendingBatchSyncFromStorage(collectionId) {
  if (!collectionId) return;
  WebStorage.clearDataFromWebStorage(
    WebStorageKey.ADD_INFLUENCER_BATCH_SYNC_KEY(collectionId),
  );
}

/**
 * When the collection list API returns during add/batch-sync, merge with the prior table
 * so placeholders and in-flight rows stay visible and sort to the top with
 * `sortInfluencerRowsForSyncDisplay`.
 * `batchFloatNewRowsActive` is true while a batch is starting, polling, or resuming from storage.
 */
function mergeCollectionInfluencerListFromApi(
  prevList,
  serverList,
  campaignPlatformId,
  batchFloatNewRowsActive,
) {
  const prev = Array.isArray(prevList) ? prevList : [];
  const server = Array.isArray(serverList) ? serverList : [];
  const prevById = new Map();
  prev.forEach((row) => {
    const id = row?.influencer_data?.influencer_id;
    if (id != null && String(id) !== "") {
      prevById.set(String(id), row);
    }
  });

  const prevHadAddFlowContext = prev.some(
    (r) =>
      r?._pendingPlaceholder === true ||
      r?.is_newly_added === true ||
      r?.detail_public_received === false,
  );

  const serverMerged = server.map((row) => {
    const id = String(row?.influencer_data?.influencer_id || "");
    const prevRow = id ? prevById.get(id) : undefined;
    let next = { ...row };
    if (prevRow) {
      next = {
        ...next,
        is_newly_added: prevRow.is_newly_added || next.is_newly_added,
        detail_public_received:
          prevRow.detail_public_received === false
            ? false
            : next.detail_public_received,
        _addFlowEnrichedAt: prevRow._addFlowEnrichedAt ?? next._addFlowEnrichedAt,
        _pendingPlaceholder: false,
        _syncHandle: prevRow._syncHandle ?? next._syncHandle,
        _placeholderOrderIndex: prevRow._placeholderOrderIndex,
      };
    } else if (
      batchFloatNewRowsActive &&
      (prev.length > 0 || prevHadAddFlowContext) &&
      id &&
      getPlatformIsUpdatedValue(row?.platforms_data, campaignPlatformId) === 0
    ) {
      // New id on server during batch (not in prior list) — float to top with sort helper
      next = {
        ...next,
        is_newly_added: true,
        detail_public_received: next.detail_public_received ?? false,
      };
    }
    return next;
  });

  const serverHandleKeys = new Set();
  serverMerged.forEach((row) => {
    const pd = Array.isArray(row.platforms_data)
      ? row.platforms_data[0]
      : row.platforms_data;
    const key = normIdentifier(pd?.identifier_id || "");
    if (key) serverHandleKeys.add(key);
  });

  const floatingPlaceholders = prev.filter((r) => {
    if (r?._pendingPlaceholder !== true) return false;
    const pid = String(r?.influencer_data?.influencer_id || "");
    if (!pid.startsWith("__pending__")) return false;
    const h = normIdentifier(r._syncHandle || "");
    return Boolean(h) && !serverHandleKeys.has(h);
  });

  return [...floatingPlaceholders, ...serverMerged];
}

// Shimmer component for loading states
const ShimmerBox = ({
  width = "100px",
  height = "20px",
  borderRadius = "4px",
}) => (
  <div
    style={{
      width,
      height,
      borderRadius,
      background:
        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
    }}
  >
    <style>
      {`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}
    </style>
  </div>
);

const CollectionInfluencerCampaignList = (props) => {
  const [start, setStart] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteCollection, setDeleteCollection] = useState({});
  const [collectionList, setCollectionList] = useState([]);
  const [priceType, setPriceType] = useState(null);
  const [collectionName, setCollectionName] = useState("");
  const [modalIsOpenDelete, setModalIsOpenDelete] = useState(false);
  const [collectionId, setCollectionId] = useState(
    collectionList[0]?.collection_id,
  );
  const [collectionInfluencerList, setCollectionInfluencerList] = useState([]);
  const [influencerInviteList, setInfluencerInviteList] = useState([]);
  const [isInviteLoading, setInviteLoading] = useState(null);
  const [hoveredIcons, setHoveredIcons] = useState([]);
  const [modalIsOpenPrice, setModalIsOpenPrice] = useState(false);
  const [addInfluencerOpen, setAddInfluencerOpen] = useState(false);
  const [influencerDetailPublic, setInfluencerDetailPublic] = useState({});
  const [totalRow, setTotalRow] = useState(0);
  const [platformHandle, setPlatformHandle] = useState("");
  const [influencerId, setInfluencerId] = useState(null);
  const [checkFollower, setCheckFollower] = useState(null);
  const [sendPriceLoading, setSendPriceLoading] = useState({});
  const [inprogressInfulencerIds, setInprogressInfluencerids] = useState({});
  const [checkInfluencerId, setCheckInfluencerId] = useState([]);
  const [openRejectPriceModal, setOpenRejectPriceModal] = useState(false);
  const [rejectPriceLoading, setRejectPriceLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openProductPopup, setOpenProductPopup] = useState(false);
  const [productId, setProductId] = useState(null);
  const [influencerPrice, setInfluencerPrice] = useState(0);
  const [clientPrice, setClientPrice] = useState(0);
  const [disabledButtons, setDisabledButtons] = useState({});
  const [assignmentList, setAssignmentList] = useState([]);
  const [collectionListLoading, setCollectionListLoading] = useState(false);
  const [initialLoader, setInitialLoader] = useState(true);
  const [bidList, setBidList] = useState([]);
  const [addInfluencerLoading, setAddInfluencerLoading] = useState(false);
  // Batch add-influencer flow state
  const [isBatchInfluencerSyncPending, setIsBatchInfluencerSyncPending] = useState(false);
  const isBatchInfluencerSyncPendingRef = useRef(false);
  const [batchProgressSnapshot, setBatchProgressSnapshot] = useState(null);
  const [batchProgressPopupPos, setBatchProgressPopupPos] = useState(null);

  const navigate = useNavigate();
  const campaignId = props.campaignId;
  const campaignDetail = props?.campaignDetail;
  console.log("campaignDetail coll", props);

  // Refs for batch flow
  const collectionInfluencerListRef = useRef([]);
  const inprogressInfulencerIdsRef = useRef({});
  const batchSyncIntervalRef = useRef(null);
  const addInfluencerBatchIdRef = useRef(null);
  const backgroundToastTimerRef = useRef(null);
  const backgroundToastIdRef = useRef(null);
  const batchSyncDetailPendingIdsRef = useRef(new Set());
  /** Prevents overlapping details-public calls for the same id during batch polling. */
  const detailPublicInFlightRef = useRef(new Set());
  const deferredBatchListRefreshRef = useRef(false);
  const batchProgressPopupIdRef = useRef(null);
  const batchProgressClearTimerRef = useRef(null);
  const batchProgressSuppressedBatchIdRef = useRef(null);
  const lastAddInfluencerParamsRef = useRef(null);
  const lastDetailRequestIdRef = useRef(null);

  const platformId =
    campaignDetail?.content_type_deep_data &&
    campaignDetail?.content_type_deep_data[0]?.platform_data?.platform_id;
  useEffect(() => {
    if (
      props?.campaignDetail?.brand_data?.org_id &&
      props?.campaignDetail?.brand_data?.brand_id
    ) {
      getCollectionList();
    }
  }, []);

  useEffect(() => {
    if (
      collectionList?.[0]?.collection_id ||
      collectionList[0]?.collection_id
    ) {
      getCollectionInfluencerList();
    }
    getInfluencerInviteList();
  }, [collectionList?.[0]?.collection_id || collectionList[0]?.collection_id]);

  // Keep refs in sync with state so async callbacks always see latest values
  useEffect(() => {
    collectionInfluencerListRef.current = collectionInfluencerList;
  }, [collectionInfluencerList]);

  useEffect(() => {
    inprogressInfulencerIdsRef.current = inprogressInfulencerIds;
  }, [inprogressInfulencerIds]);

  useEffect(() => {
    isBatchInfluencerSyncPendingRef.current = isBatchInfluencerSyncPending;
  }, [isBatchInfluencerSyncPending]);

  // Track batch progress popup ID for position reset
  useEffect(() => {
    const id = batchProgressSnapshot?.batch_id;
    if (id && id !== batchProgressPopupIdRef.current) {
      batchProgressPopupIdRef.current = id;
      setBatchProgressPopupPos(null);
    }
    if (!id) {
      batchProgressPopupIdRef.current = null;
    }
  }, [batchProgressSnapshot?.batch_id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backgroundToastTimerRef.current) clearTimeout(backgroundToastTimerRef.current);
      if (batchSyncIntervalRef.current) clearInterval(batchSyncIntervalRef.current);
      if (batchProgressClearTimerRef.current) clearTimeout(batchProgressClearTimerRef.current);
    };
  }, []);

  // Resume batch profile-sync popup + polling if a batch was in progress before reload/close
  useEffect(() => {
    const collectionId =
      collectionList?.[0]?.collection_id ?? collectionList[0]?.collection_id;
    if (!collectionId) return;

    const pending = readPendingBatchSyncFromStorage(collectionId);
    if (!pending?.batch_id) return;

    Core.getBatchSyncStatus(
      (response) => {
        const responseRoot = response?.response || response || {};
        const summary = responseRoot?.summary || {};
        const done = isBatchSyncFinished(summary, responseRoot);
        if (done) {
          clearPendingBatchSyncFromStorage(collectionId);
          return;
        }
        const effectiveBatchId = responseRoot?.batch_id || pending.batch_id;
        setBatchProgressSnapshot({
          ...responseRoot,
          batch_id: effectiveBatchId,
        });
        setBatchSyncPending(true);
        startBatchSyncPolling(effectiveBatchId);
      },
      () => {
        clearPendingBatchSyncFromStorage(collectionId);
      },
      () => {},
      { batch_id: pending.batch_id },
    );
  }, [collectionList?.[0]?.collection_id]);

  function updateProgress(status) {
    setLoading(status);
  }

  function getCollectionList(options) {
    const silent = options?.silent === true;
    const params = {
      campaign_id: campaignId,
      org_id: props?.campaignDetail?.brand_data?.org_id,
      brand_id: props?.campaignDetail?.brand_data?.brand_id,
    };

    console.log("params", params);
    Core.getCollectionList(
      getCollectionListSuccess,
      getCollectionListFailed,
      silent ? () => {} : updateProgress,
      params,
    );
  }

  function getCollectionListSuccess(response_data) {
    setCollectionList(response_data?.collection_list);
    console.log("response_data0", response_data);
  }

  console.log("totalRow", totalRow);

  function getCollectionListFailed(errorList) {
    toast.error(errorList?.map((item) => item?.m).join(", "));
  }

  function getBidList() {
    var params = {
      campaign_id: campaignId,
    };

    Core.getBidList(getBidListSuccess, getBidListFailed, () => {}, params);
  }

  function getBidListSuccess(response_data) {
    setBidList(response_data["bid_list"]);
  }

  function getBidListFailed(errorList) {
    toast.error(errorList.map((item) => item.m).join(", "));
  }

  function getAssignmentUser() {
    var params = {
      campaign_id: campaignId,
    };

    Core.getAssignmentUser(
      getAssignmentUserSuccess,
      getAssignmentUserFailed,
      () => {},
      params,
    );
  }

  function getAssignmentUserSuccess(response_data) {
    setAssignmentList(response_data["assignment_list"]);
  }

  function getAssignmentUserFailed(errorList) {
    toast.error(errorList.map((err) => err.m).join(", "));
  }

  function getCollectionInfluencerList(options) {
    const silent = options?.silent === true;
    console.log("collectionList", collectionList[0]?.collection_id);
    const params = {
      collection_id: collectionList[0]?.collection_id,
      platform_id: collectionList[0]?.platform_id,
    };
    Core.getCollectionInfluencerList(
      getCollectionInfluencerListSuccess,
      getCollectionInfluencerListFailed,
      silent ? () => {} : (status) => setCollectionListLoading(status),
      params,
    );
  }

  function getCollectionInfluencerListSuccess(response_data) {
    const serverList =
      response_data?.collection_deep_data?.collection_influencer_list || [];
    const batchFloatNewRowsActive =
      batchSyncIntervalRef.current != null ||
      isBatchInfluencerSyncPendingRef.current ||
      Boolean(addInfluencerBatchIdRef.current);

    setCollectionInfluencerList((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const merged = mergeCollectionInfluencerListFromApi(
        prevList,
        serverList,
        platformId,
        batchFloatNewRowsActive,
      );
      return sortInfluencerRowsForSyncDisplay(merged);
    });
    getAssignmentUser();
    getBidList();
    console.log("response_data8", response_data);
    setTotalRow(response_data?.pagination_data?.count || 0);
    setCollectionListLoading(false);
    setInitialLoader(false);
  }

  function getCollectionInfluencerListFailed(errorList) {
    toast.error(errorList?.map((item) => item?.m).join(", "));
    setCollectionInfluencerList([]);
    setCollectionListLoading(false);
    setInitialLoader(false);
  }

  function getInfluencerInviteList() {
    var params = {
      campaign_id: props.campaignId,
    };
    Core.getInfluencerInviteList(
      getInfluencerInviteListSuccess,
      getInfluencerInviteListFailed,
      () => {},
      params,
    );
  }

  function getInfluencerInviteListSuccess(response_data) {
    setInfluencerInviteList(response_data?.invite_list);
  }

  function getInfluencerInviteListFailed(errorList) {
    toast.error(errorList);
  }

  function postCollectionDelete() {
    const params = {
      collection_id:
        collectionList?.[0]?.collection_id || collectionList[0]?.collection_id,
    };
    Core.postCollectionDelete(
      postCollectionDeleteSuccess,
      postCollectionDeleteFailed,
      (status) => setDeleteCollection({ [collectionId]: status }),
      params,
    );
  }

  function postCollectionDeleteSuccess(response_data) {
    getCollectionList();
    toast.success("Collection Deleted Successfully");
    closeDelete();
  }

  function postCollectionDeleteFailed(errorList) {
    toast.error(errorList?.map((item) => item?.m).join(", "));
  }

  function addInprogressInfluencerid(influencer_id) {
    setInprogressInfluencerids((prev) => {
      const updated = { ...prev };
      const addId = (id) => {
        if (!updated[id]) {
          updated[id] = {
            name: false,
            profile_pic: false,
            followers: false,
            content_count: false,
            engagement_rate: false,
          };
        }
      };
      if (Array.isArray(influencer_id)) {
        influencer_id.forEach(addId);
      } else {
        addId(influencer_id);
      }
      return updated;
    });
  }

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "var(--general-secondary-color)",
        color: "var(--gray-gray-300)",
        fontSize: "16px",
        fontWeight: 600,
        lineHight: "19.2px",
        border: "none",
      },
    },
    headCells: {
      style: {
        padding: "10px 0px 10px 20px",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: 400,
        lineHight: "19.2px",
        "&:hover": {
          backgroundColor: "var(--general-secondary-color)",
          fontWeight: 600,
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "20px",
      },
    },
  };

  function openDelete() {
    if (modalIsOpenDelete == false) {
      setModalIsOpenDelete(true);
    } else {
      setModalIsOpenDelete(false);
    }
  }

  function closeDelete() {
    setModalIsOpenDelete(false);
  }

  function inviteAndReject(id) {
    let inviteId = influencerInviteList?.find(
      (item) => item?.influencer_id == id,
    );
    return inviteId;
  }

  const platFormName = (platforms_data, influencer_id) => {
    const handleMouseEnter = (index) => {
      const newHoveredIcons = [...hoveredIcons];
      newHoveredIcons[index] = true;
      setHoveredIcons(newHoveredIcons);
    };

    const handleMouseLeave = (index) => {
      const newHoveredIcons = [...hoveredIcons];
      newHoveredIcons[index] = false;
      setHoveredIcons(newHoveredIcons);
    };

    const platformData = platforms_data;

    const handlePlatformClick = (url) => {
      // Open the platform URL here
      window.open(url, "_blank");
      // Reset all hover states to false after clicking
      setHoveredIcons(new Array(platforms_data?.length).fill(false));
    };

    return (
      <>
        {platforms_data?.length > 0 &&
          platformData &&
          platforms_data?.platform_id != null && (
            <div>
              {/* <CustomText
              p
              text={"Social Presence"}
              fontSize={"font_size_small"}
              textColor={"text_color_black"}
              fontWeight={"Poppins_semiBold"}
            /> */}
            </div>
          )}
        <div style={{ marginTop: 7 }} className="flex-row">
          <div
            className={"platform-name"}
            onMouseEnter={() =>
              handleMouseEnter(`${influencer_id}${platformData?.platform_id}`)
            }
            onMouseLeave={() =>
              handleMouseLeave(`${influencer_id}${platformData?.platform_id}`)
            }
            onClick={() =>
              handlePlatformClick(
                Utils.getSocialUrl(
                  platformData?.platform_id,
                  platformData?.identifier_id,
                ),
              )
            }
          >
            {platformData?.platform_id && (
              <Icon
                iconName={
                  hoveredIcons[`${influencer_id}${platformData?.platform_id}`]
                    ? Utils.getSocialIconHover(platformData?.platform_id)
                    : Utils.getSocialIcon(platformData?.platform_id)
                }
                height={14}
                width={14}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  const getSelectedPlatformData = (platforms_data) => {
    return platforms_data;
  };

  const formatPlatformData = (platforms_data, key) => {
    const selectedPlatformData = getSelectedPlatformData(platforms_data);

    return Utils.changeNumberToInternationalUnit(selectedPlatformData?.[key]);
  };

  function findRowIndexForPublicDetail(list, influencerId, platformsDataObj) {
    const targetId = String(influencerId || "");
    let idx = (list || []).findIndex(
      (item) => String(item?.influencer_data?.influencer_id) === targetId,
    );
    let pendingIdToClear;
    if (idx === -1 && platformsDataObj?.identifier_id) {
      const want = normIdentifier(platformsDataObj.identifier_id);
      idx = (list || []).findIndex(
        (item) =>
          item?._pendingPlaceholder === true &&
          want === normIdentifier(item?._syncHandle),
      );
      if (idx !== -1) {
        pendingIdToClear = list[idx]?.influencer_data?.influencer_id;
      }
    }
    if (idx === -1 && platformsDataObj?.identifier_id) {
      const want = normIdentifier(platformsDataObj.identifier_id);
      idx = (list || []).findIndex((item) => {
        const id = String(item?.influencer_data?.influencer_id || "");
        if (id.startsWith("__pending__")) return false;
        const pd = getPlatformDataForCampaign(item?.platforms_data, platformId);
        return want === normIdentifier(pd?.identifier_id);
      });
    }
    return { index: idx, pendingIdToClear };
  }

  function clearPendingInfluencerIdFromProgress(pendingId) {
    if (!pendingId || !String(pendingId).startsWith("__pending__")) return;
    queueMicrotask(() => {
      setInprogressInfluencerids((pi) => {
        const n = { ...pi };
        delete n[pendingId];
        inprogressInfulencerIdsRef.current = n;
        return n;
      });
    });
  }

  /** Shimmer until platform `is_updated` resolves or per-field detail-public flags complete. */
  function isFieldBeingUpdated(data, field) {
    const isUpdated = getPlatformIsUpdatedValue(data?.platforms_data, platformId);
    if (isUpdated === 0) {
      return true;
    }
    if (typeof isUpdated === "number" && isUpdated !== 0) {
      return false;
    }
    const influencerId = data?.influencer_data?.influencer_id;
    const progressEntry = influencerId ? inprogressInfulencerIds[influencerId] : undefined;
    const progressStatus = getProgressStatus(progressEntry);
    if (!progressEntry || progressStatus === "COMPLETED") {
      return false;
    }
    if (typeof progressEntry === "string") {
      return progressEntry !== "COMPLETED";
    }
    if (typeof progressEntry === "object") {
      if (typeof progressEntry.status === "string") {
        return progressEntry.status !== "COMPLETED";
      }
      return !progressEntry[field];
    }
    return true;
  }

  const tableInfluencerSchema = [
    {
      name: "Influencer Name",
      selector: (row) => row?.influencer_data?.name ?? "",
      width: "200px",
      cell: (data) => {
        const isNameLoading = isFieldBeingUpdated(data, "name");
        const isPicLoading = isFieldBeingUpdated(data, "profile_pic");
        const rawPd = data?.platforms_data;
        const pdName = Array.isArray(rawPd) ? rawPd[0] : rawPd || {};
        const handleRaw = data?._syncHandle || pdName?.identifier_id || "";
        const handleDisp =
          platformId === "btll"
            ? `@${String(handleRaw).replace(/^@+/, "")}`
            : handleRaw;
        const displayName =
          (data?.influencer_data?.name &&
            String(data.influencer_data.name).trim()) ||
          handleDisp ||
          "";

        return (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isPicLoading ? (
              <ShimmerBox width="40px" height="40px" borderRadius="50%" />
            ) : (
              <Image
                source={
                  data?.influencer_data?.profile_picture
                    ? Utils.generateInfluencerDpUrl(
                        data?.influencer_data?.influencer_id,
                        data?.influencer_data?.profile_picture,
                      )
                    : require("../../resources/images/user-copy.png")
                }
                className="collection-user-profile"
              />
            )}
            <div style={{ flexDirection: "row", display: "flex", gap: 5 }}>
              <div>
                <div
                  style={{
                    flexDirection: "row",
                    display: "flex",
                    gap: 5,
                    alignItems: "baseline",
                  }}
                >
                  {isNameLoading && !displayName ? (
                    <ShimmerBox width="120px" height="16px" />
                  ) : (
                    <CustomText p text={displayName} className="Body-1" />
                  )}
                </div>
              </div>
            </div>
          </span>
        );
      },
    },
    {
      name: "Social Presence",
      selector: (row) =>
        platFormName(row?.platforms_data, row?.influencer_data?.influencer_id),
      cell: (data) => {
        if (isFieldBeingUpdated(data, "followers")) {
          return <ShimmerBox width="36px" height="18px" borderRadius="4px" />;
        }
        return platFormName(
          data?.platforms_data,
          data?.influencer_data?.influencer_id,
        );
      },
    },

    {
      name: "Followers",
      selector: (row) => formatPlatformData(row?.platforms_data, "followers"),
      sortable: false,
      cell: (data) => {
        if (isFieldBeingUpdated(data, "followers")) {
          return <ShimmerBox width="70px" height="18px" />;
        }
        return formatPlatformData(data?.platforms_data, "followers");
      },
    },
    {
      name: "Avg Views",
      selector: (row) =>
        Utils.isObjectOrNullUndefinedZero(
          formatPlatformData(row?.platforms_data, "avg_views"),
        )
          ? "-"
          : formatPlatformData(row?.platforms_data, "avg_views"),
      cell: (data) => {
        if (isFieldBeingUpdated(data, "avg_views")) {
          return <ShimmerBox width="70px" height="18px" />;
        }
        return Utils.isObjectOrNullUndefinedZero(
          formatPlatformData(data?.platforms_data, "avg_views"),
        )
          ? "-"
          : formatPlatformData(data?.platforms_data, "avg_views");
      },
    },
    {
      name: "ER",
      selector: (row) =>
        Utils.isNullOrUndefinedOrZero(
          formatPlatformData(row?.platforms_data, "engagement_rate"),
        )
          ? "-"
          : formatPlatformData(row?.platforms_data, "engagement_rate"),
      cell: (data) => {
        if (isFieldBeingUpdated(data, "engagement_rate")) {
          return <ShimmerBox width="60px" height="18px" />;
        }
        return (
          <span>
            <Tooltip
              title={
                <>
                  <div>
                    Avg Likes ={" "}
                    {formatPlatformData(data?.platforms_data, "avg_likes")}
                  </div>
                  <div>
                    Avg Comments ={" "}
                    {formatPlatformData(data?.platforms_data, "avg_comments")}
                  </div>
                </>
              }
            >
              {Utils.isNullOrUndefinedOrZero(
                Number(
                  formatPlatformData(data?.platforms_data, "engagement_rate"),
                ),
              )
                ? "-"
                : Number(
                    formatPlatformData(data?.platforms_data, "engagement_rate"),
                  ).toFixed(2) + " %"}
            </Tooltip>
          </span>
        );
      },
    },
    {
      name: "Internal Cost",
      selector: (row) =>
        row?.influencer_data?.internal_price
          ? row?.influencer_data?.internal_price
          : "...",
      sortable: false,

      cell: (data) => {
        const isAssigned = assignmentList?.some(
          (assignment) =>
            assignment?.influencer_data?.influencer_id ===
            data?.influencer_data?.influencer_id,
        );

        return (
          <Tooltip
            title={
              isAssigned ? "Cannot change price after contract is done" : ""
            }
            arrow
            placement="top"
          >
            <span
              style={{
                color: "rgb(25, 119, 242)",
                display: "flex",
                alignItems: "center",
              }}
              className="flex-row"
            >
              <CustomText
                p
                text={
                  data?.influencer_data?.internal_price
                    ? data?.influencer_data?.internal_price
                    : "..."
                }
                className="m-r-6"
              />
              {!isAssigned ? (
                <div
                  onClick={() => {
                    setModalIsOpenPrice(true);
                    setInfluencerId(data?.influencer_data?.influencer_id);
                    setPriceType("internal_price");
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Icon iconName="EditIconCounter" />
                </div>
              ) : null}
            </span>
          </Tooltip>
        );
      },
    },
    {
      name: "Client Cost",
      selector: (row) =>
        row?.influencer_data?.client_price
          ? row?.influencer_data?.client_price
          : "...",
      sortable: false,

      cell: (data) => {
        const isAssigned = assignmentList?.some(
          (assignment) =>
            assignment?.influencer_data?.influencer_id ===
            data?.influencer_data?.influencer_id,
        );

        return (
          <Tooltip
            title={
              isAssigned ? "Cannot change price after contract is done" : ""
            }
            arrow
            placement="top"
          >
            <span
              style={{
                color: "rgb(25, 119, 242)",
                display: "flex",
                alignItems: "center",
              }}
              className="flex-row"
            >
              <CustomText
                p
                text={
                  data?.influencer_data?.client_price
                    ? data?.influencer_data?.client_price
                    : "..."
                }
                className="m-r-6"
              />
              {!isAssigned && (
                <div
                  onClick={() => {
                    setModalIsOpenPrice(true);
                    setPriceType("price");
                    setInfluencerId(data?.influencer_data?.influencer_id);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Icon iconName="EditIconCounter" />
                </div>
              )}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  tableInfluencerSchema.push({
    name: "",
    cell: (data) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {data?.invite_data?.status !== "REJECTED" && (
          <span style={{ marginBottom: "8px" }}>
            <Tooltip
              title={
                bidList?.some(
                  (bid) =>
                    bid?.influencer_deep_data?.influencer_data
                      ?.influencer_id === data?.influencer_data?.influencer_id,
                ) ||
                assignmentList?.some(
                  (assignment) =>
                    assignment?.influencer_data?.influencer_id ===
                    data?.influencer_data?.influencer_id,
                )
                  ? "Bid already placed"
                  : ""
              }
              arrow
              placement="top"
            >
              <span>
                {(() => {
                  const isBidPlaced =
                    bidList?.some(
                      (bid) =>
                        bid?.influencer_deep_data?.influencer_data
                          ?.influencer_id ===
                        data?.influencer_data?.influencer_id,
                    ) ||
                    assignmentList?.some(
                      (assignment) =>
                        assignment?.influencer_data?.influencer_id ===
                        data?.influencer_data?.influencer_id,
                    );

                  return (
                    <PrimaryCta
                      fifth
                      fontSize={"font_size_large"}
                      text={isBidPlaced ? "Price Sent" : "Send Price"}
                      isLoading={
                        sendPriceLoading[data?.influencer_data?.influencer_id]
                      }
                      disabled={
                        !data?.influencer_data?.client_price ||
                        disabledButtons[data?.influencer_data?.influencer_id] ||
                        isBidPlaced
                      }
                      onClick={() => {
                        const influencerId = data?.influencer_data?.influencer_id;

                        // Disable button immediately to prevent multiple clicks
                        setDisabledButtons((prev) => ({
                          ...prev,
                          [influencerId]: true,
                        }));

                        setInfluencerId(influencerId);
                        setInfluencerPrice(data?.influencer_data?.internal_price);
                        setClientPrice(data?.influencer_data?.client_price);
                        if (campaignDetail?.selected_products?.length > 0) {
                          setOpenProductPopup(true);
                        } else {
                          sendPriceDirectly(
                            influencerId,
                            data?.influencer_data?.client_price,
                            data?.influencer_data?.internal_price,
                          );
                        }
                      }}
                    />
                  );
                })()}
              </span>
            </Tooltip>
          </span>
        )}
      </div>
    ),
    maxwidth: "100px",
  });

  const initialPlatformId =
    props.campaignDetail?.content_type_deep_data?.[0]?.platform_data
      ?.platform_id || props?.collectionPlatformId;

  const getPlatformLabel = (platformId) => {
    return platformId === "btll" ? "Instagram" : "YouTube";
  };

  const defaultPlatform =
    typeof initialPlatformId === "object"
      ? initialPlatformId
      : initialPlatformId
        ? {
            platform_id: initialPlatformId,
            label: getPlatformLabel(initialPlatformId),
          }
        : null;

  function updateInfluencerInList(detailResponse) {
    const influencerData =
      detailResponse?.influencer_deep_data?.influencer_data;
    const platformsDataArr =
      detailResponse?.influencer_deep_data?.platforms_data;
    const categorysData = detailResponse?.influencer_deep_data?.categorys_data;
    if (!influencerData?.influencer_id) return;

    const platformsDataObj = getPlatformDataForCampaign(
      platformsDataArr,
      platformId,
    );

    const updatedInfluencerData = {
      name: platformsDataObj?.full_name,
      ...influencerData,
    };

    setCollectionInfluencerList((prevList) => {
      const list = Array.isArray(prevList) ? prevList : [];
      const { index: updatedIndex, pendingIdToClear } =
        findRowIndexForPublicDetail(
          list,
          influencerData.influencer_id,
          platformsDataObj,
        );

      if (updatedIndex === -1) return list;

      const newList = [...list];
      const [updatedItem] = newList.splice(updatedIndex, 1);
      if (pendingIdToClear) {
        clearPendingInfluencerIdFromProgress(pendingIdToClear);
      } else if (
        String(updatedItem?.influencer_data?.influencer_id || "").startsWith(
          "__pending__",
        )
      ) {
        clearPendingInfluencerIdFromProgress(
          String(updatedItem.influencer_data.influencer_id),
        );
      }

      const updatedItemWithNewData = {
        ...updatedItem,
        _pendingPlaceholder: false,
        is_newly_added: false,
        detail_public_received: true,
        _addFlowEnrichedAt: Date.now(),
        influencer_data: {
          ...updatedInfluencerData,
        },
        platforms_data: platformsDataObj,
        categorys_data: categorysData || updatedItem?.categorys_data,
      };

      newList.unshift(updatedItemWithNewData);
      return sortInfluencerRowsForSyncDisplay(newList);
    });
  }

  function maybeFlushDeferredBatchListRefresh(detailInfluencerId) {
    if (
      detailInfluencerId &&
      batchSyncDetailPendingIdsRef.current.has(detailInfluencerId)
    ) {
      batchSyncDetailPendingIdsRef.current.delete(detailInfluencerId);
    }
    if (
      deferredBatchListRefreshRef.current &&
      batchSyncDetailPendingIdsRef.current.size === 0
    ) {
      deferredBatchListRefreshRef.current = false;
      getCollectionInfluencerList();
    }
  }

  function getInfluencerDetailPublic(id, options = {}) {
    if (String(id || "").startsWith("__pending__")) {
      return;
    }
    const rid = id;
    lastDetailRequestIdRef.current = rid;
    if (options.fromBatchSync) {
      if (detailPublicInFlightRef.current.has(rid)) {
        return;
      }
      detailPublicInFlightRef.current.add(rid);
      batchSyncDetailPendingIdsRef.current.add(rid);
    }
    var params = { influencer_id: id };
    Core.getInfluencerDetailPublic(
      getInfluencerDetailPublicSuccess,
      getInfluencerDetailPublicFailed,
      () => {},
      params,
    );
  }

  function getInfluencerDetailPublicSuccess(response) {
    setInfluencerDetailPublic(response);

    const influencerId =
      response?.influencer_deep_data?.influencer_data?.influencer_id;
    if (!influencerId) {
      const rid = lastDetailRequestIdRef.current;
      if (rid) detailPublicInFlightRef.current.delete(rid);
      return;
    }

    const platformsForCampaign =
      response?.influencer_deep_data?.platforms_data;
    const isUpdatedVal = getPlatformIsUpdatedValue(
      platformsForCampaign,
      platformId,
    );

    const listSnapshot = collectionInfluencerListRef.current || [];
    const row = listSnapshot.find(
      (item) =>
        String(item?.influencer_data?.influencer_id) === String(influencerId),
    );
    const shouldMergeFromPublicForBatchAdd =
      row?.is_newly_added === true ||
      row?._pendingPlaceholder === true ||
      row?.detail_public_received === false;

    if (isUpdatedVal === 0) {
      updateInfluencerInList(response);
      setCheckFollower(null);
      maybeFlushDeferredBatchListRefresh(influencerId);
      detailPublicInFlightRef.current.delete(influencerId);
      return;
    }

    if (shouldMergeFromPublicForBatchAdd) {
      updateInfluencerInList(response);
    } else {
      getCollectionInfluencerList({ silent: true });
    }

    const platformData = getPlatformDataForCampaign(
      platformsForCampaign,
      platformId,
    );
    const name = platformData?.full_name;
    const profile_pic = platformData?.profile_pic;
    const followers = platformData?.followers;
    const content_count = platformData?.content_count;
    const engagement_rate = platformData?.engagement_rate;
    const profileReady =
      profile_pic && profile_pic !== "PENDING_UPLOAD";

    setInprogressInfluencerids((prev) => {
      const updated = { ...prev };
      if (
        !updated[influencerId] ||
        updated[influencerId] === "PENDING" ||
        updated[influencerId] === "IN_PROGRESS"
      ) {
        updated[influencerId] = {
          name: false,
          profile_pic: false,
          followers: false,
          content_count: false,
          engagement_rate: false,
        };
      }
      if (typeof updated[influencerId] === "object") {
        if (name) updated[influencerId].name = true;
        if (profileReady) updated[influencerId].profile_pic = true;
        if (followers) updated[influencerId].followers = true;
        if (content_count) updated[influencerId].content_count = true;
        if (engagement_rate) updated[influencerId].engagement_rate = true;
        const flags = updated[influencerId];
        if (
          flags.name &&
          flags.profile_pic &&
          flags.followers &&
          flags.content_count &&
          flags.engagement_rate
        ) {
          updated[influencerId] = { status: "COMPLETED" };
        }
      }
      inprogressInfulencerIdsRef.current = updated;
      return updated;
    });
    setCheckFollower(null);
    maybeFlushDeferredBatchListRefresh(influencerId);
    detailPublicInFlightRef.current.delete(influencerId);
  }

  function getInfluencerDetailPublicFailed(errorList) {
    const rid = lastDetailRequestIdRef.current;
    if (rid) {
      detailPublicInFlightRef.current.delete(rid);
    }
    if (rid && batchSyncDetailPendingIdsRef.current.has(rid)) {
      batchSyncDetailPendingIdsRef.current.delete(rid);
    }
    if (
      deferredBatchListRefreshRef.current &&
      batchSyncDetailPendingIdsRef.current.size === 0
    ) {
      deferredBatchListRefreshRef.current = false;
      getCollectionInfluencerList();
    }
    toast.error(
      Array.isArray(errorList)
        ? errorList.map((item) => item?.m).filter(Boolean).join(", ")
        : String(errorList || "Request failed"),
    );
  }

  // ─── Batch sync helpers ────────────────────────────────────────────────────

  function stopBatchSyncPolling() {
    if (batchSyncIntervalRef.current) {
      clearInterval(batchSyncIntervalRef.current);
      batchSyncIntervalRef.current = null;
    }
  }

  function setBatchSyncPending(pending) {
    const b = Boolean(pending);
    isBatchInfluencerSyncPendingRef.current = b;
    setIsBatchInfluencerSyncPending(b);
  }

  function finishBatchSyncFinalized() {
    setBatchSyncPending(false);
    stopBatchSyncPolling();
    addInfluencerBatchIdRef.current = null;
    const collectionIdForBatch =
      collectionList?.[0]?.collection_id ?? collectionList[0]?.collection_id;
    if (collectionIdForBatch) {
      clearPendingBatchSyncFromStorage(collectionIdForBatch);
    }
    deferredBatchListRefreshRef.current = true;
    if (batchSyncDetailPendingIdsRef.current.size === 0) {
      deferredBatchListRefreshRef.current = false;
      getCollectionInfluencerList();
    }
  }

  function dismissBatchProgressPopup() {
    const id = batchProgressSnapshot?.batch_id;
    if (id) batchProgressSuppressedBatchIdRef.current = id;
    if (batchProgressClearTimerRef.current) {
      clearTimeout(batchProgressClearTimerRef.current);
      batchProgressClearTimerRef.current = null;
    }
    setBatchProgressSnapshot(null);
  }

  function pollBatchSyncStatus(batchId) {
    if (!batchId) return;

    Core.getBatchSyncStatus(
      (response) => {
        const responseRoot = response?.response || response || {};
        const summary = responseRoot?.summary || {};
        const done = isBatchSyncFinished(summary, responseRoot);
        const effectiveBatchId = responseRoot?.batch_id || batchId;

        if (batchProgressSuppressedBatchIdRef.current !== effectiveBatchId) {
          setBatchProgressSnapshot({ ...responseRoot, batch_id: effectiveBatchId });
        }

        // Merge job state into rows, then fetch details-public for newly resolved IDs (once each)
        const { list: updatedList, inprog: updatedInprog, detailsToFetch } =
          applyBatchSyncJobsToRows(
            collectionInfluencerListRef.current,
            inprogressInfulencerIdsRef.current,
            responseRoot?.jobs || [],
          );
        setCollectionInfluencerList(updatedList);
        setInprogressInfluencerids(updatedInprog);

        // Details-public on every successful poll until this batch finishes (inFlight avoids overlap per id).
        detailsToFetch.forEach((realId) => {
          if (String(realId || "").startsWith("__pending__")) return;
          getInfluencerDetailPublic(realId, { fromBatchSync: true });
        });

        // Refresh collection + influencer list from server on every successful batch-sync poll
        getCollectionList({ silent: true });
        getCollectionInfluencerList({ silent: true });

        if (done) {
          if (batchProgressClearTimerRef.current) {
            clearTimeout(batchProgressClearTimerRef.current);
            batchProgressClearTimerRef.current = null;
          }
          if (batchProgressSuppressedBatchIdRef.current !== effectiveBatchId) {
            batchProgressClearTimerRef.current = window.setTimeout(() => {
              setBatchProgressSnapshot(null);
              batchProgressClearTimerRef.current = null;
              batchProgressSuppressedBatchIdRef.current = null;
            }, 5000);
          }
          finishBatchSyncFinalized();
        }
      },
      () => {
        // Keep polling on transient errors
      },
      () => {},
      { batch_id: batchId },
    );
  }

  function startBatchSyncPolling(batchId) {
    if (!batchId) return;
    const collectionIdForBatch =
      collectionList?.[0]?.collection_id ?? collectionList[0]?.collection_id;
    if (collectionIdForBatch) {
      writePendingBatchSyncToStorage(collectionIdForBatch, batchId);
    }
    addInfluencerBatchIdRef.current = String(batchId);
    detailPublicInFlightRef.current = new Set();
    if (batchProgressClearTimerRef.current) {
      clearTimeout(batchProgressClearTimerRef.current);
      batchProgressClearTimerRef.current = null;
    }
    batchProgressSuppressedBatchIdRef.current = null;
    stopBatchSyncPolling();
    pollBatchSyncStatus(addInfluencerBatchIdRef.current);
    batchSyncIntervalRef.current = window.setInterval(() => {
      pollBatchSyncStatus(addInfluencerBatchIdRef.current);
    }, 3000);
  }

  function handleQueuedInfluencerPlaceholders({ handles, batchId, platformId: pId }) {
    if (!Array.isArray(handles) || handles.length === 0) return;
    const effectivePlatformId = pId || platformId;
    const safeBatch = batchId || "batch";
    const placeholders = handles.map((handle, i) => ({
      _pendingPlaceholder: true,
      _syncHandle: handle,
      _placeholderOrderIndex: i,
      is_newly_added: true,
      detail_public_received: false,
      influencer_data: {
        influencer_id: makePendingInfluencerId(safeBatch, handle),
        name: handle,
        profile_picture: null,
      },
      platforms_data: {
        platform_id: effectivePlatformId,
        identifier_id: handle,
        is_updated: 0,
      },
      invite_data: null,
    }));

    setInprogressInfluencerids((prev) => {
      const u = { ...prev };
      placeholders.forEach((row) => {
        const iid = row.influencer_data.influencer_id;
        u[iid] = {
          name: false,
          profile_pic: false,
          followers: false,
          content_count: false,
          engagement_rate: false,
        };
      });
      inprogressInfulencerIdsRef.current = u;
      return u;
    });

    setCollectionInfluencerList((prev) => {
      const filtered = (prev || []).filter(
        (r) =>
          !r?._pendingPlaceholder ||
          !handles.some((h) => normIdentifier(r?._syncHandle) === normIdentifier(h)),
      );
      return sortInfluencerRowsForSyncDisplay([...placeholders, ...filtered]);
    });
  }

  // ─── Batch add submit ────────────────────────────────────────────────────────

  const MAX_INFLUENCERS_ADD_PER_BATCH = 200;

  function normalizeHandle(raw) {
    if (!raw) return "";
    let input = raw.trim();
    input = input.replace(/^['"]|['"]$/g, "");
    input = input.replace(/^@+/, "");

    if (
      /^(https?:\/\/|www\.|.*(instagram\.com|youtube\.com|youtu\.be))/i.test(input)
    ) {
      let urlStr = input;
      if (!/^https?:\/\//i.test(urlStr)) {
        urlStr = "https://" + urlStr;
      }
      try {
        const url = new URL(urlStr);
        const host = url.hostname.toLowerCase();
        let path = url.pathname.replace(/\/+/g, "/").replace(/^\/+|\/+$/g, "");
        path = path.split("?")[0].split("#")[0];

        if (host.includes("instagram.com")) {
          const first = path.split("/")[0];
          const disallowed = ["p", "reel", "explore", "stories", "accounts", "about", "directory"];
          if (first && !disallowed.includes(first)) {
            return first.replace(/^@+/, "");
          }
        } else if (host.includes("youtube.com")) {
          if (path.startsWith("@")) {
            return path.split("/")[0].replace(/^@+/, "");
          }
          const parts = path.split("/");
          if (parts[0] === "channel" && parts[1]) return parts[1];
          if (parts[0] === "c" && parts[1]) return parts[1];
          if (parts[0] === "user" && parts[1]) return parts[1];
        } else if (host.includes("youtu.be")) {
          return "";
        }
      } catch (e) {}
    }
    return input.replace(/\s+/g, "");
  }

  function validationAddInfluencer() {
    if (!platformHandle || platformHandle.trim() === "") {
      toast.error("Please enter social handle(s)");
      return false;
    }
    const handles = platformHandle
      .split(",")
      .map((handle) => normalizeHandle(handle))
      .filter((handle) => handle.length > 0);

    if (!handles || handles.length === 0) {
      toast.error("Please enter at least one valid handle");
      return false;
    }
    return true;
  }

  function postAddNewInfluencer() {
    if (!validationAddInfluencer()) return;

    let handles = platformHandle
      .split(",")
      .map((h) => normalizeHandle(h))
      .filter((h) => h.length > 0);
    handles = Array.from(new Set(handles));

    if (handles.length === 0) {
      toast.error("Please enter at least one valid handle");
      return;
    }

    if (handles.length > MAX_INFLUENCERS_ADD_PER_BATCH) {
      toast.error(`Maximum ${MAX_INFLUENCERS_ADD_PER_BATCH} influencers can be added at a time`);
      return;
    }

    const existingRows = countExistingInfluencerRowsForAddLimit(collectionInfluencerList);
    if (existingRows + handles.length > MAX_INFLUENCERS_ADD_PER_BATCH) {
      const cap = MAX_INFLUENCERS_ADD_PER_BATCH - existingRows;
      toast.error(
        `This list can include at most ${MAX_INFLUENCERS_ADD_PER_BATCH} influencers. You already have ${existingRows}, so you can add at most ${Math.max(0, cap)} more (you entered ${handles.length}).`,
      );
      return;
    }

    setAddInfluencerLoading(true);

    if (backgroundToastTimerRef.current) clearTimeout(backgroundToastTimerRef.current);
    backgroundToastTimerRef.current = window.setTimeout(() => {
      if (addInfluencerLoading) {
        toast.info("Influencers are being added in the background.");
      }
    }, 4000);

    const params = {
      collection_id: collectionList[0]?.collection_id,
      identifier_ids: handles,
      platform_id:
        typeof defaultPlatform === "object"
          ? defaultPlatform?.platform_id
          : defaultPlatform || platformId,
    };
    lastAddInfluencerParamsRef.current = params;

    Core.postAddInfluencer(
      (res, response) => postAddInfluencerSuccess(response),
      (errorList) => postAddInfluencerFail(errorList),
      () => {},
      params,
    );
  }

  function postAddInfluencerSuccess(response) {
    if (backgroundToastTimerRef.current) {
      clearTimeout(backgroundToastTimerRef.current);
      backgroundToastTimerRef.current = null;
    }

    const responseRoot = response?.response || response || {};
    const batchIdFromAdd = responseRoot?.batch_id ?? response?.batch_id;
    const summary = responseRoot?.summary || {};
    const addedCount = Number(summary?.queued_for_add ?? summary?.total_submitted ?? 0);
    const brandNewArr = Array.isArray(responseRoot.brand_new) ? responseRoot.brand_new : [];
    const alreadyInDb = Array.isArray(responseRoot.already_in_db_not_in_collection)
      ? responseRoot.already_in_db_not_in_collection
      : [];
    const handlesFromResponse = mergeQueuedInfluencerHandles(brandNewArr, alreadyInDb);
    const submittedIds = lastAddInfluencerParamsRef.current?.identifier_ids || [];
    const queuedForAdd = Number(summary?.queued_for_add ?? 0);
    const pendingHandles =
      handlesFromResponse.length > 0
        ? handlesFromResponse
        : queuedForAdd > 0
          ? mergeQueuedInfluencerHandles(submittedIds)
          : [];

    const apiMessage = typeof responseRoot.message === "string" && responseRoot.message.trim();
    if (apiMessage) {
      toast.success(apiMessage);
    } else {
      toast.success(`${addedCount} influencer${addedCount === 1 ? "" : "s"} added to the list.`);
    }

    if (pendingHandles.length > 0) {
      handleQueuedInfluencerPlaceholders({
        handles: pendingHandles,
        batchId: batchIdFromAdd,
        platformId: lastAddInfluencerParamsRef.current?.platform_id,
      });
    }

    if (batchIdFromAdd) {
      setBatchSyncPending(true);
      startBatchSyncPolling(batchIdFromAdd);
    } else {
      const collectionIdForBatch =
        collectionList?.[0]?.collection_id ?? collectionList[0]?.collection_id;
      if (collectionIdForBatch) {
        clearPendingBatchSyncFromStorage(collectionIdForBatch);
      }
      setBatchProgressSnapshot(null);
    }

    setAddInfluencerLoading(false);
    setPlatformHandle("");
    setAddInfluencerOpen(false);
  }

  function postAddInfluencerFail(errorList) {
    if (backgroundToastTimerRef.current) {
      clearTimeout(backgroundToastTimerRef.current);
      backgroundToastTimerRef.current = null;
    }

    const errorMessage =
      errorList?.[0]?.m ||
      errorList?.err_l?.map((item) => item?.m).filter(Boolean).join(", ") ||
      errorList?.message ||
      "Failed to add influencers";

    toast.error(errorMessage);
    setAddInfluencerLoading(false);
  }

  // ─── Batch progress popup ───────────────────────────────────────────────────

  function formatBatchJobLabel(job) {
    const h = String(job?.identifier_id || job?.influencer_id || "").trim();
    const pid = job?.platform_id;
    if (!h) return "—";
    if (String(pid) === "btll") return `@${h.replace(/^@+/, "")}`;
    return h;
  }

  function renderBatchJobStateBadge(job) {
    const kind = getBatchJobDisplayKind(job);
    const baseStyle = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, flexShrink: 0 };
    const dot = (color, glow) => (
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: color, boxShadow: glow || undefined }} />
    );
    if (kind === "completed" || kind === "completed_with_issue") {
      return <span style={{ ...baseStyle, fontWeight: 700, color: kind === "completed_with_issue" ? "#a16207" : "#0d7d4a" }}><span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>✓</span>{kind === "completed_with_issue" ? "Completed (issue)" : "Completed"}</span>;
    }
    if (kind === "prioritised") return <span style={{ ...baseStyle, color: "#5b21b6" }}><span aria-hidden="true" style={{ fontSize: 12 }}>★</span>Prioritized</span>;
    if (kind === "waiting") return <span style={{ ...baseStyle, color: "#1d4ed8" }}>{dot("#60a5fa")}Waiting</span>;
    if (kind === "failed") return <span style={{ ...baseStyle, color: "#b91c1c" }}><span aria-hidden="true" style={{ fontSize: 12 }}>!</span>Unavailable</span>;
    // active or unknown
    return <span style={{ ...baseStyle, color: "#b45309" }}>{dot("#f59e0b", "0 0 0 2px rgba(245,158,11,0.25)")}Active</span>;
  }

  function onBatchProgressPopupDragStart(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    const panel = e.currentTarget.closest("[data-batch-progress-panel]");
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const startLeft = batchProgressPopupPos ? batchProgressPopupPos.x : rect.left;
    const startTop = batchProgressPopupPos ? batchProgressPopupPos.y : rect.top;
    const originX = e.clientX;
    const originY = e.clientY;
    const w = rect.width;
    const h = rect.height;
    function onMove(ev) {
      const dx = ev.clientX - originX;
      const dy = ev.clientY - originY;
      let l = Math.max(8, Math.min(startLeft + dx, window.innerWidth - w - 8));
      let t = Math.max(8, Math.min(startTop + dy, window.innerHeight - h - 8));
      setBatchProgressPopupPos({ x: l, y: t });
    }
    function onUp() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function renderBatchProgressPopup() {
    if (!batchProgressSnapshot) return null;
    const summary = batchProgressSnapshot.summary || {};
    const jobs = Array.isArray(batchProgressSnapshot.jobs) ? batchProgressSnapshot.jobs : [];
    const totalFromSummary = Number(summary.total);
    const completedOnly = Math.max(0, Number(summary.completed) || 0);
    const completedWithIssue = Math.max(0, Number(summary.completed_with_issue ?? summary.completedWithIssue) || 0);
    const combinedFromSummary = completedOnly + completedWithIssue;
    const hasSummaryTotal = Number.isFinite(totalFromSummary) && totalFromSummary > 0;
    const totalForBar = hasSummaryTotal ? totalFromSummary : jobs.length > 0 ? jobs.length : 0;
    const settledFromJobs = jobs.filter((j) => isBatchJobComplete(j)).length;
    const settledForBar = totalForBar > 0
      ? Math.min(totalForBar, hasSummaryTotal ? combinedFromSummary : settledFromJobs)
      : 0;
    const isComplete = batchProgressSnapshot.is_complete === true;
    const fillPct = isComplete ? 100 : totalForBar > 0 ? Math.min(100, (settledForBar / totalForBar) * 100) : 0;

    const nCompleted = hasSummaryTotal
      ? combinedFromSummary
      : "completed" in summary
        ? (Number(summary.completed) || 0) + (Number(summary.completed_with_issue ?? 0) || 0)
        : jobs.filter((j) => isBatchJobComplete(j)).length;
    let nActive, nPrioritized, nWaiting;
    if (jobs.length > 0) {
      nActive = jobs.filter((j) => getBatchJobDisplayKind(j) === "active").length;
      nPrioritized = jobs.filter((j) => getBatchJobDisplayKind(j) === "prioritised").length;
      nWaiting = jobs.filter((j) => getBatchJobDisplayKind(j) === "waiting").length;
    } else {
      nActive = Math.max(0, Number(summary.active) || 0);
      nPrioritized = Math.max(0, Number(summary.prioritised ?? summary.prioritized) || 0);
      nWaiting = Math.max(0, Number(summary.waiting) || 0);
    }
    const stillRunning = nActive + nPrioritized + nWaiting;
    const progressLabel = totalForBar > 0
      ? `${settledForBar} / ${totalForBar} synced`
      : nCompleted > 0
        ? `${nCompleted} profile${nCompleted === 1 ? "" : "s"} synced`
        : "Preparing…";

    const positionStyle = batchProgressPopupPos
      ? { left: batchProgressPopupPos.x, top: batchProgressPopupPos.y, right: "auto", bottom: "auto" }
      : { right: 24, bottom: 24, left: "auto", top: "auto" };

    return (
      <div
        data-batch-progress-panel
        style={{
          position: "fixed",
          zIndex: 10050,
          width: 320,
          maxWidth: "calc(100vw - 32px)",
          background: "var(--reelax-surface, #fff)",
          borderRadius: 8,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
          padding: 14,
          fontFamily: "inherit",
          ...positionStyle,
        }}
        role="status"
        aria-live="polite"
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <div
            onMouseDown={onBatchProgressPopupDragStart}
            role="presentation"
            style={{ flex: 1, minWidth: 0, cursor: "move", userSelect: "none" }}
            title="Drag to move"
          >
            <CustomText p fontSize="font_size_medium" textColor="text_color_primary" text="Profile sync" textAlign="text_align_left" />
          </div>
          <button
            type="button"
            onClick={dismissBatchProgressPopup}
            aria-label="Close batch progress"
            style={{ border: "none", background: "rgba(0,0,0,0.04)", borderRadius: 6, width: 28, height: 28, lineHeight: 1, cursor: "pointer", fontSize: 18, color: "rgba(0,0,0,0.55)" }}
          >×</button>
        </div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6, letterSpacing: "0.02em" }}>Progress</div>
          <div
            role="progressbar"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(fillPct)}
            style={{ height: 8, borderRadius: 4, background: "rgba(0,0,0,0.08)", overflow: "hidden" }}
          >
            <div style={{ height: "100%", width: `${fillPct}%`, borderRadius: 4, background: isComplete ? "#0d7d4a" : "var(--reelax-primary, #2a6edb)", transition: "width 0.35s ease, background 0.25s ease" }} />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <CustomText p fontSize="font_size_small" textColor="text_color_secondary" text={progressLabel} textAlign="text_align_left" />
          {isComplete && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "#0d7d4a" }}>
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>✓</span>Completed
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 12, marginBottom: 8 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, color: "#0d7d4a" }}>
            <span aria-hidden="true" style={{ fontSize: 13 }}>✓</span>Completed: {nCompleted}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "#b45309" }}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
            Active: {nActive}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "#5b21b6" }}>
            <span aria-hidden="true" style={{ fontSize: 12 }}>★</span>Prioritized: {nPrioritized}
          </span>
          {nWaiting > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "#1d4ed8" }}>
              <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "#60a5fa", display: "inline-block" }} />
              Waiting: {nWaiting}
            </span>
          )}
        </div>
        {!isComplete && stillRunning > 0 && (
          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 8 }}>
            {stillRunning} profile{stillRunning === 1 ? "" : "s"} still in progress
          </div>
        )}
        {jobs.length > 0 && (
          <div style={{ maxHeight: 180, overflow: "auto", borderTop: "1px solid rgba(0,0,0,0.06)", marginTop: 4, paddingTop: 8 }}>
            {jobs.map((job, idx) => {
              const key = job?.job_id || `${job?.identifier_id || "j"}-${idx}`;
              return (
                <div
                  key={key}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: idx < jobs.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={formatBatchJobLabel(job)}>
                    {formatBatchJobLabel(job)}
                  </span>
                  {renderBatchJobStateBadge(job)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function flyoutBidListFilter() {
    return (
      <Sidebar
        isOpen={addInfluencerOpen}
        sidebarWidth={"480px"}
        onClose={() => setAddInfluencerOpen(false)}
        Header={filterSidebarHeader}
        Footer={filterSidebarFooter}
        headerAlignment="left"
      >
        <div style={{ padding: 20 }}>
          <div className="edit-campaign-popup-p flex-column">
            {/* Platform */}
            <div className="edit-campaign-popup-c" style={{ gap: 10 }}>
              <div style={{ marginBottom: 10 }}>
                <CustomText
                  p
                  fontSize={"font_size_medium"}
                  textColor={"text_color_primary"}
                  text={"Platform"}
                  textAlign={"text_align_left"}
                />
              </div>
              <div>
                <CustomTextInput
                  type="text"
                  disabled={true}
                  value={defaultPlatform?.label || ""}
                />
              </div>
            </div>

            {/* Handle */}
            <div style={{ textAlign: "left", gap: 10, marginTop: 20 }}>
              <div style={{ marginBottom: 10 }}>
                <CustomText
                  p
                  fontSize={"font_size_medium"}
                  textColor={"text_color_primary"}
                  text={`${defaultPlatform?.label || "Social"} Handle`}
                  textAlign={"text_align_left"}
                />
              </div>
              <div>
                <CustomTextArea
                  placeholder={`Enter ${defaultPlatform?.label || "social"} handle(s) or profile URL(s). Comma-separated. You can add many at once (e.g. 20–200); each handle appears immediately in the list while data loads, up to the ${MAX_INFLUENCERS_ADD_PER_BATCH} per-batch limit.`}
                  rows={12}
                  onChange={(e) => setPlatformHandle(e.target.value)}
                  value={platformHandle}
                />
              </div>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  const filterSidebarHeader = () => <div className="typographyH7">Add Influencer</div>;

  const filterSidebarFooter = () => (
    <div style={{ display: "flex", flex: 1, gap: 20 }}>
      <div style={{ flex: 1 }}>
        <PrimaryCta
          fullWidth
          onClick={postAddNewInfluencer}
          fontSize={"font_size_large"}
          text={"Add Influencer"}
          isLoading={addInfluencerLoading}
        />
      </div>
    </div>
  );

  function sendPriceUpdateProgress(influencer_id, status) {
    setSendPriceLoading((prev) => ({ ...prev, [influencer_id]: status }));
  }

  function sendPriceDirectly(influencer_id, client_price, influencer_price) {
    if (!client_price) {
      toast.error("Client price is not set for this influencer");
      return;
    }
    if (!influencer_price) {
      toast.error("Influencer price is not set for this influencer");
      return;
    }

    let params = {
      campaign_id: campaignId,
      influencer_id: influencer_id,
      bid_data: JSON.stringify({
        type: "cash",
        value: client_price,
        proposal_note: "",
        influencer_price: influencer_price,
      }),
    };
    if (selectedProducts.length > 0) {
      params.product_ids = JSON.stringify(selectedProducts);
    }
    console.log("Sending price params:", params);
    Core.sendPrice(
      () => sendPriceSuccess(influencer_id),
      sendPriceFailed,
      (status) => sendPriceUpdateProgress(influencer_id, status),
      params,
    );
  }

  function sendPriceSuccess(influencer_id) {
    toast.success("Price sent successfully");
    setSendPriceLoading((prev) => ({ ...prev, [influencer_id]: false }));
    getInfluencerInviteList();
    getCollectionInfluencerList();
    setOpenProductPopup(false);
  }
  function sendPriceFailed(errorList) {
    toast.error(errorList.map((item) => item.m).join(", "));
  }

  return (
    <div style={{ minHeight: 500, marginTop: "30px" }}>
      {renderBatchProgressPopup()}
      <div
        style={{
          display: "flex",
          justifyContent: "right",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <PrimaryCta
          onClick={() => setAddInfluencerOpen(true)}
          fontSize={"font_size_regular"}
          invertSmall
          // className="create-campaign"
          text={"Add Influencer"}
        />
      </div>
      <div className="">
        {initialLoader ? (
          <div className="fall-back-center-campaign">
            <Loader />
          </div>
        ) : totalRow == 0 &&
          collectionListLoading == true &&
          initialLoader == false ? (
          <div style={{ width: "100%" }} className="fall-back-center-campaign">
            <FallBackBtn
              heading={
                "Oops!! you don't have any influencers in this list yet."
              }
              title={
                "You can add your shortlisted influencers in this list, so that you can share them with anyone and get feedback"
              }
              onClick={() => setAddInfluencerOpen(true)}
              text={"Add Influencer"}
            />
          </div>
        ) : (
          <div className="custom-table-bid">
            <CustomTable
              className="CustomTable"
              tableSchema={tableInfluencerSchema}
              tableData={collectionInfluencerList}
              rowPaddingTop={16}
              rowPaddingBottom={16}
              customStyles={customStyles}
              progressComponent={
                <div className="fall-back-center-campaign">
                  <Loader />
                </div>
              }
              paginationTotalRows={totalRow}
              paginationPerPage={10}
              pagination
            />
          </div>
        )}
      </div>
      {/* {modalIsOpen && (
        <CreateCollectionModal
          closeModal={closeModal}
          getCollectionList={getCollectionList}
          campaignId={campaignId}
          org_id={props?.campaignDetail?.brand_data?.org_id}
          brand_id={props?.campaignDetail?.brand_data?.brand_id}
        />
      )
  } */}
      {flyoutBidListFilter()}
      {openRejectPriceModal && (
        <ModalComponent
          isOpen={openRejectPriceModal}
          onRequestClose={() => setOpenRejectPriceModal(false)}
          customStyle={{
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999999,
            },
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              backgroundColor: "#F0F3F9",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
              overflowY: "none",
              height: "30%",
              width: "30%",
            },
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <CustomText
                p
                text={`Reject Price Request`}
                textColor={"#040408"}
              />
            </div>
            <div
              onClick={() => {
                setOpenRejectPriceModal(false);
                setInfluencerId(null);
              }}
              className=""
              style={{ cursor: "pointer" }}
            >
              <Icon iconName="CloseRoundButtonIcon" />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 30,
            }}
          >
            <CustomText
              p
              text={`Are you sure you want to reject this price request?`}
              textColor={"#040408"}
              className="m-b-12"
            />
            <div style={{ marginTop: 30, display: "flex", gap: 16 }}>
              <PrimaryCta
                third
                fontSize={"font_size_regular"}
                text={"Reject"}
                // onClick={() => {
                //   rejectPriceRequest();
                // }}
                isLoading={rejectPriceLoading}
              />
            </div>
          </div>
        </ModalComponent>
      )}
      {modalIsOpenPrice &&
        (collectionId !== null || collectionId !== undefined) && (
          <PriceToCollectionInfluencer
            closePriceModal={() => setModalIsOpenPrice(false)}
            collectionId={collectionList[0]?.collection_id}
            influencerId={influencerId}
            collectionInfluencerList={collectionInfluencerList}
            setCollectionInfluencerList={setCollectionInfluencerList}
            priceType={priceType}
          />
        )}
      <Modal
        isOpen={modalIsOpenDelete}
        onRequestClose={closeDelete}
        style={CancelReminder}
      >
        <>
          <div className="contract-cancellation-confirm">
            <h2 className="cancel-text">Delete Collection</h2>
            <p className="cancel-request">
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}>{collectionName}</span>?
            </p>

            <div className="btn-accept-reject">
              <div className="confirm-cancelliation">
                <>
                  <PrimaryCta
                    third
                    onClick={() => postCollectionDelete()}
                    fontSize={"font_size_regular"}
                    className="create-campaign"
                    text={"Delete"}
                    isLoading={deleteCollection[collectionId]}
                  />

                  <button
                    className="confirm-btn-bid  m-l-32"
                    onClick={() => closeDelete()}
                  >
                    Cancel
                  </button>
                </>
              </div>
              <ToastContainer />
            </div>
          </div>
        </>
      </Modal>
      {openProductPopup && (
        <FreeTrailPopup
          onClose={() => setOpenProductPopup(false)}
          content={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: 20,
              }}
            >
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                Select Product
              </div>

              {/* Info */}
              <div style={{ textAlign: "left", marginBottom: 10 }}>
                {`You can choose max ${campaignDetail?.max_products_count} product(s)`}
              </div>

              {/* Product List */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {campaignDetail?.selected_products?.map((item) => {
                  const productId = item?.product_id;
                  const isSelected = selectedProducts.includes(productId);

                  return (
                    <div key={productId}>
                      <div
                        className={
                          isSelected
                            ? "collection-grid-card-active"
                            : "collection-grid-card"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!productId) return;

                          setSelectedProducts((prev) =>
                            prev.includes(productId)
                              ? prev.filter((id) => id !== productId)
                              : [...prev, productId],
                          );
                        }}
                      >
                        <div style={{ display: "flex", gap: 20 }}>
                          {/* Image */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              height: 100,
                              flex: 0.4,
                            }}
                          >
                            <img
                              src={Utils.generateProductUrl(
                                item?.org_id,
                                item?.product_image,
                              )}
                              alt={item?.title}
                            />
                          </div>

                          {/* Content */}
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              className="collection-name-container"
                              title={item?.title}
                            >
                              <CustomText
                                p
                                text={item?.title}
                                className="collection-grid-collection-name truncate-3-lines"
                              />
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div className="collection-grid-date gap-10">
                                <CustomText
                                  p
                                  text={`Rs. ${item?.final_price}`}
                                  className="typographyH5"
                                />
                                {item?.product_weight && (
                                  <CustomText
                                    p
                                    text={item?.product_weight}
                                    className="typographyH5"
                                  />
                                )}
                              </div>

                              {/* Delete Icon */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProductId(productId);
                                }}
                                style={{ cursor: "pointer", marginTop: 10 }}
                              >
                                <Icon iconName="deleteHoverIcon" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="divider" />
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <div style={{ marginTop: 20 }}>
                <PrimaryCta
                  fifth
                  fontSize={"font_size_large"}
                  text={"Send Price"}
                  isLoading={sendPriceLoading[influencerId]}
                  onClick={() => {
                    // CTA-only validation
                    if (!selectedProducts.length) {
                      toast.error("Please select at least one product");
                      return;
                    }

                    if (
                      selectedProducts.length >
                      campaignDetail?.max_products_count
                    ) {
                      toast.error(
                        `You can select max ${campaignDetail?.max_products_count} products`,
                      );
                      return;
                    }

                    // Disable button immediately to prevent multiple clicks
                    setDisabledButtons((prev) => ({
                      ...prev,
                      [influencerId]: true,
                    }));

                    sendPriceDirectly(
                      influencerId,
                      clientPrice,
                      influencerPrice,
                    );
                  }}
                />
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default CollectionInfluencerCampaignList;

const CancelReminder = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    background: "var(--general-secondary-color)",
    textAlign: "center",
    zIndex: "5",
    height: "40%",
    display: "flex",
    flexDirection: "column",
    minWidth: "460px",
    minHeight: "200px",
  },
  overlay: {
    background: "rgba(0, 0, 0, 0.95)",
    zIndex: 10,
  },
};
